// src/components/RichTextRenderer/RichTextRenderer.tsx
import React from 'react';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  INLINES,
  MARKS,
  Document as RichTextDocument,
} from '@contentful/rich-text-types';

interface RichTextRendererProps {
  document: RichTextDocument;
}

const options: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children) => (
      <h1 className="text-4xl font-bold my-4">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (_node, children) => (
      <h2 className="text-3xl font-semibold my-3">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_node, children) => (
      <h3 className="text-2xl font-medium my-2">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_node, children) => (
      <h4 className="text-xl font-medium my-2">{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (_node, children) => (
      <h5 className="text-lg font-medium my-1">{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (_node, children) => (
      <h6 className="text-base font-medium my-1">{children}</h6>
    ),

    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <p className="mb-4 leading-relaxed">{children}</p>
    ),
    [BLOCKS.QUOTE]: (_node, children) => (
      <blockquote className="border-l-4 pl-4 italic my-4">
        {children}
      </blockquote>
    ),

    [BLOCKS.UL_LIST]: (_node, children) => (
      <ul className="list-disc list-inside mb-4">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node, children) => (
      <ol className="list-decimal list-inside mb-4">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_node, children) => (
      <li className="ml-4 mb-2">{children}</li>
    ),

    [BLOCKS.HR]: () => <hr className="my-6 border-gray-300" />,

    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      // Example fallback for any embedded entry
      return (
        <div className="my-4">
          [Embedded entry: {node.data.target.sys.contentType.sys.id}]
        </div>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = (node.data.target as any).fields;
      const url = asset.file.url.startsWith('//')
        ? `https:${asset.file.url}`
        : asset.file.url;
      return <img src={url} alt={asset.title} className="my-4 mx-auto" />;
    },

    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={(node.data as any).uri}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline"
      >
        {children}
      </a>
    ),
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const slug = (node.data.target as any).fields.slug;
      return (
        <a href={`/blog/${slug}`} className="underline">
          {children}
        </a>
      );
    },
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      const asset = (node.data.target as any).fields.file;
      const url = asset.url.startsWith('//') ? `https:${asset.url}` : asset.url;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {children}
        </a>
      );
    },
  },
};

const RichTextRenderer: React.FC<RichTextRendererProps> = ({ document }) => (
  <div>{documentToReactComponents(document, options)}</div>
);

export default RichTextRenderer;
